<template>
  <div class="cart-sidebar relative z-100">
    <Overlay :visible="isOpen" @click="closeSidebar" />
    <Drawer
      ref="reviewsSidebarRef"
      class="reviews-sidebar"
      :is-open="isOpen"
      data-test-id="reviews-sidebar"
      @click:close="closeSidebar"
    >
      <template #header>
        <div class="reviews-sidebar__top">
          <div class="reviews-sidebar__header">
            <div
              v-if="currentStep === 'detail' && !isBackButtonHidden"
              class="back"
              @click="onBack"
            >
              <IconBack />
            </div>
            <div class="reviews-sidebar__title-row">
              <span class="label">
                {{
                  reviewType === 'event'
                    ? t('review.title')
                    : t('review.partner.title')
                }}
              </span>
              <span
                v-if="currentStep === 'list' && averageRating"
                class="reviews-sidebar__rating"
              >
                <ReviewRatingStars
                  class="reviews-sidebar__rating-stars"
                  star-classes="h-4"
                  :average-rating="
                    parseFloat(averageRating.toString().replace(',', '.')) || 0
                  "
                />
                <span class="reviews-sidebar__rating-value">
                  {{ t('review.sidebar.rating', { n: averageRating }) }}
                </span>
              </span>
            </div>
          </div>
        </div>
      </template>

      <LazyReviewSidebarBody
        v-if="hasContentBeenOpened"
        :reviews="reviews"
        :average-rating="averageRating"
        :review-count="reviewCount"
        :selected-review-id="selectedReviewId"
        :event-permalink="eventPermalink"
        :review-breakdown="reviewBreakdown"
        :initial-rating-filter="initialRatingFilter"
        :is-open="isOpen"
        :current-step="currentStep"
        @update:current-step="currentStep = $event"
        @update:selected-review-id="emit('update:selectedReviewId', $event)"
        @close="closeSidebar"
      />
    </Drawer>
  </div>
</template>

<script lang="ts" setup>
import type { Review } from '~/composables/types/event'
import IconBack from '~/assets/icons/back.svg'

interface Props {
  reviews: Review[]
  isOpen: boolean
  averageRating: string
  reviewCount: number
  selectedReviewId?: string
  isBackButtonHidden?: boolean
  bottomButtonLink?: string
  reviewType: 'event' | 'partner' | 'third-party'
  eventPermalink?: string
  reviewBreakdown?: Record<string, number>
  initialRatingFilter?: number | null
}

const { t } = useI18n()

const props = withDefaults(defineProps<Props>(), {
  reviews: () => [],
  isOpen: false,
  averageRating: '',
  reviewCount: 0,
  selectedReviewId: undefined,
  isBackButtonHidden: false,
  bottomButtonLink: '',
  reviewType: 'event',
  eventPermalink: undefined,
  reviewBreakdown: undefined,
  initialRatingFilter: undefined,
})

const emit = defineEmits(['close', 'update:selectedReviewId'])

const currentStep = ref<'list' | 'detail'>('list')
const hasContentBeenOpened = ref(false)

const setBodyScrollLocked = (locked: boolean) => {
  if (!import.meta.client) return

  document.body.classList.remove('prevent-scroll')
  if (locked) {
    document.body.classList.add('prevent-scroll')
  }
}

watch(
  () => props.isOpen,
  (open) => {
    if (open) {
      hasContentBeenOpened.value = true
    }
    setBodyScrollLocked(open)
  },
  { immediate: true }
)

const onBack = () => {
  currentStep.value = 'list'
  emit('update:selectedReviewId', undefined)
}

const closeSidebar = () => {
  setTimeout(() => {
    currentStep.value = 'list'
  }, 500)
  emit('close')
}
</script>

<style lang="css" scoped>
@reference '~/assets/css/tailwind.css';
.reviews-sidebar {
  @apply w-full md:max-w-[552px] p-0 overflow-y-scroll flex flex-col;

  &__top {
    @apply relative flex gap-2 items-center px-6 py-4 w-full top-0 border-b border-gray-200;
  }

  &__header {
    @apply w-full flex justify-start items-center;
  }

  &__title-row {
    @apply flex items-center gap-2 flex-1 min-w-0;
  }

  &__header:has(.back) &__title-row {
    @apply pl-8;
  }

  .label {
    @apply text-lg font-semibold text-left;
  }

  &__rating {
    @apply flex items-center gap-1.5 shrink-0;
  }

  &__rating-value {
    @apply text-base text-stone-600;
  }

  .back {
    @apply absolute left-2 top-0 bottom-0 my-auto cursor-pointer w-[22px] h-[22px] shrink-0;

    svg {
      @apply w-full h-full;
    }
  }
}
</style>

<style>
body.prevent-scroll {
  overflow: hidden;
}
</style>
